// © Microsoft Corporation. All rights reserved.

/**
 * Client side properties that Render Service sends to initialize RequestData.
 * These properties are typically passed via query param to the render service.
 * On the client side these are available and populated through the DOM.
 */
export interface RequestProps {

    /**
     * URL for the original client request.
     */
    href: string;

    /**
     * Height of client browser window.
     */
    innerHeight: number;

    /**
     * Device pixel ratio of client.
     */
    devicePixelRatio: number;

    /**
     * CSS Grid support of the client browser.
     */
    canUseCssGrid: boolean;

    /**
     * Request Id.
     */
    requestId: string;

    /**
     * The request cookie string
     */
    cookie: string;

    /**
     * The request referer string
     */
    referer: string;
}

/**
 * Singleton class to store client browser data sent through query param to Render Service.
 * This is only used under server environment.
 */
class RequestData {
    /**
     * The single instance of the RequestData class.
     * @type {RequestData}
     */
    private static _instance: RequestData = null;

    /**
     * URL object to be used to store original request url.
     */
    public readonly url: URL;

    /**
     * Inner height of browser from the original request.
     */
    public readonly innerHeight: number;

    /**
     * Device pixel ratio from the original request.
     */
    public readonly devicePixelRatio: number;

    /**
     * Boolean flag that represents if CSS Grid is supported by the browser from the original request.
     */
    public readonly canUseCssGrid: boolean;

    /**
     * Request Id.
     */
    public readonly requestId: string;

    /**
     * Request cookie string
     */
    public readonly cookie: string;

    /**
     * Request referer string
     */
    public readonly referer: string;

    /**
     * Initialize the request data instance with passed in client properties
     */
    private constructor(data: RequestProps) {
        this.url = new URL(data.href);
        this.innerHeight = data.innerHeight;
        this.devicePixelRatio = data.devicePixelRatio;
        this.canUseCssGrid = data.canUseCssGrid;
        this.requestId = data.requestId;
        this.cookie = data.cookie;
        this.referer = data.referer;
    }

    /**
     * Returns singleton class instance.
     * @returns RequestData
     */
    public static getInstance(): RequestData {
        if (global["TEST_ENV"] && !RequestData._instance) {
            resetRequestData({
                href: "http://localhost:8080/",
                innerHeight: 768,
                devicePixelRatio: 1,
                canUseCssGrid: false,
                requestId: "0",
                cookie: "",
                referer: ""
            });
        }

        if (!RequestData._instance) {
            // Throw error if the request data has not been set as this should never be the case.
            // This likely highlights that the request data was not initialized when handling a
            // request when server side rendering.
            // TODO - http://osgvsowi/24832691 renable throwing an error.
            // This is currently disabled as other singletons that are initialized at import time use
            // this they will throw an error before anyone has the chance to initialize the request data
            resetRequestData({
                href: "http://localhost:8080/",
                innerHeight: 0,
                devicePixelRatio: 0,
                canUseCssGrid: false,
                requestId: "0",
                cookie: "",
                referer: ""
            });
            // throw new Error("Request data is not initialized yet through ResetInstance.");
        }
        return RequestData._instance;
    }

    /**
     * Reset singleton class instance with passed in info and client settings
     * @returns RequestData
     */
    public static resetInstance(data: RequestProps): RequestData {
        RequestData._instance = new RequestData(data);
        return RequestData._instance;
    }
}

/**
 * Export function to reset the request data singleton.
 * This must be used to set the request data.
 */
export const resetRequestData = RequestData.resetInstance;

/**
 * Export get instance function for testing
 */
export const getRequestData = () => (RequestData.getInstance());